import { useEffect, useState } from 'react';
import valueClassname from 'src/compass/shared/util/valueClassname';
import CasTextField from '../../CasTextField/CasTextField';
import CasNumberField from '../../CasTextField/CasNumberField';
import CasSelect from '../../CasSelect/CasSelect';
import CasAutoCompleteAsync from '../../CasAutoComplete/CasAutocompleteAsync';
import CasDatePicker from '../../CasDatePicker/CasDatePicker';
import CasDateRangePicker from '../../CasDatePicker/CasDateRangePicker';
import defaultLabelFromField from '../../../util/defaultLabelFromField';

export const filterLabel = (filter: any) => filter.label ?? defaultLabelFromField(filter.name);

/*
  Inline toolbar controls are sized to their label so the row stays compact. In the
  filter drawer there is no row to keep tight, so controls span the drawer width —
  otherwise a narrow select clips longer option text.
*/
const controlSx = (label: string, fullWidth?: boolean) =>
  fullWidth ? { width: '100%' } : { minWidth: Math.max(85, label.length * 8) };

/*
 * Async autocomplete filter. The filter value sent to the query is the selected
 * option's id. We also remember the selected option object so the input can show its
 * name (not the id) when a list's id and name differ (e.g. Customer).
 */
const AutocompleteFilter = ({ filter, label, currentValue, standardProps, fullWidth }: any) => {
  const [selected, setSelected] = useState<any>(null);

  // Drop the remembered option when the value is cleared/reset externally.
  useEffect(() => {
    if (currentValue == null) setSelected(null);
  }, [currentValue]);

  const displayValue =
    selected != null && String(selected.id) === String(currentValue) ? selected : currentValue;

  return (
    <CasAutoCompleteAsync
      {...standardProps}
      url={filter.url}
      defaultValue={displayValue}
      onValueChange={(event, value: any) => {
        setSelected(value);
        standardProps.onChange(event, value?.id);
      }}
      // CasAutoCompleteAsync writes its own `sx={{ width }}`, so a caller sx is ignored -
      // stretch it through the `width` prop it actually reads.
      width={fullWidth ? '100%' : undefined}
      sx={controlSx(label, fullWidth)}
      {...(filter?.componentProps ?? {})}
    />
  );
};

/*
 * Render one filter control. `value` is the current value and `onValue(value)` emits
 * changes, so the same control works both inline (live) and staged in the advanced
 * filter drawer. Shared by CasTableToolbar and CasFilterDrawer.
 */
const renderFilter = (
  filter: any,
  value: any,
  onValue: (value: any) => void,
  opts: { fullWidth?: boolean } = {}
) => {
  const { fullWidth } = opts;
  const label = filterLabel(filter);
  const standardProps = {
    className: valueClassname(value),
    name: filter.name,
    key: filter.name,
    label,
    autoFocus: filter.autoFocus,
    onChange: (_: any, v: any) => onValue(v),
    ...(fullWidth ? { fullWidth: true } : {}),
  };

  const Component = filter?.component;
  switch (filter.type) {
    case 'component':
      return (
        <Component
          {...standardProps}
          includeAllOption={filter?.includeAllOption}
          includeNoneOption={filter?.includeNoneOption}
          includeEmptyOption={filter?.includeEmptyOption}
          value={value ?? null}
          sx={controlSx(label, fullWidth)}
          {...(filter?.componentProps ?? {})}
        />
      );
    case 'select':
      return (
        <CasSelect
          {...standardProps}
          value={value ?? null}
          sx={controlSx(label, fullWidth)}
          options={filter.options}
          optionsNameField={filter.optionsNameField}
          optionsIdField={filter.optionsIdField}
        />
      );
    case 'number':
      return <CasNumberField {...standardProps} value={value ?? ''} debounce={filter.debounce} />;
    case 'autocomplete':
      return (
        <AutocompleteFilter
          key={filter.name}
          filter={filter}
          label={label}
          currentValue={value ?? null}
          standardProps={standardProps}
          fullWidth={fullWidth}
        />
      );
    case 'date':
      return <CasDatePicker {...standardProps} value={value ?? null} />;
    case 'date-range':
      return (
        <CasDateRangePicker
          {...standardProps}
          value={value ?? null}
          {...(filter?.componentProps ?? {})}
        />
      );
    default:
      return (
        <CasTextField
          {...standardProps}
          value={value ?? ''}
          debounce={filter.debounce}
          sanitize={filter.sanitize}
        />
      );
  }
};

export default renderFilter;
